import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MedicationCard = ({ medication, onEdit, onDiscontinue, onRefill }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'expiring':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'discontinued':
        return 'bg-muted text-muted-foreground border-border';
      case 'critical':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getAlertIcon = (alertLevel) => {
    switch (alertLevel) {
      case 'critical':
        return 'AlertCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'info':
        return 'Info';
      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 transition-smooth hover:shadow-elevation-2">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base md:text-lg font-semibold text-foreground truncate">
              {medication?.name}
            </h3>
            {medication?.alertLevel && (
              <Icon 
                name={getAlertIcon(medication?.alertLevel)} 
                size={18} 
                color={medication?.alertLevel === 'critical' ? 'var(--color-destructive)' : 'var(--color-warning)'} 
              />
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-1">{medication?.genericName}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <span className="font-medium">{medication?.dosage}</span>
            <span>•</span>
            <span>{medication?.frequency}</span>
            <span>•</span>
            <span>{medication?.route}</span>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(medication?.status)}`}>
          {medication?.status?.charAt(0)?.toUpperCase() + medication?.status?.slice(1)}
        </span>
      </div>
      {medication?.alertMessage && (
        <div className={`flex items-start gap-2 p-3 rounded-lg mb-4 ${
          medication?.alertLevel === 'critical' ?'bg-destructive/10 border border-destructive/20' :'bg-warning/10 border border-warning/20'
        }`}>
          <Icon 
            name={getAlertIcon(medication?.alertLevel)} 
            size={16} 
            color={medication?.alertLevel === 'critical' ? 'var(--color-destructive)' : 'var(--color-warning)'} 
            className="flex-shrink-0 mt-0.5"
          />
          <p className={`text-xs md:text-sm ${
            medication?.alertLevel === 'critical' ? 'text-destructive' : 'text-warning'
          }`}>
            {medication?.alertMessage}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
        <div>
          <p className="text-muted-foreground mb-1">Prescribed By</p>
          <p className="font-medium text-foreground">{medication?.prescribedBy}</p>
        </div>
        <div>
          <p className="text-muted-foreground mb-1">Start Date</p>
          <p className="font-medium text-foreground">{medication?.startDate}</p>
        </div>
        <div>
          <p className="text-muted-foreground mb-1">Refills Remaining</p>
          <p className="font-medium text-foreground">{medication?.refillsRemaining} of {medication?.totalRefills}</p>
        </div>
        <div>
          <p className="text-muted-foreground mb-1">Last Filled</p>
          <p className="font-medium text-foreground">{medication?.lastFilled}</p>
        </div>
      </div>
      {medication?.instructions && (
        <div className="bg-muted rounded-lg p-3 mb-4">
          <p className="text-xs md:text-sm text-foreground">
            <span className="font-medium">Instructions: </span>
            {medication?.instructions}
          </p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="Edit"
          iconPosition="left"
          onClick={() => onEdit(medication)}
        >
          Edit
        </Button>
        {medication?.status === 'active' && medication?.refillsRemaining > 0 && (
          <Button
            variant="default"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => onRefill(medication)}
          >
            Request Refill
          </Button>
        )}
        {medication?.status === 'active' && (
          <Button
            variant="destructive"
            size="sm"
            iconName="XCircle"
            iconPosition="left"
            onClick={() => onDiscontinue(medication)}
          >
            Discontinue
          </Button>
        )}
      </div>
    </div>
  );
};

export default MedicationCard;